import { useState } from "react";
import { useNavigate } from "react-router";
import type { Item, ViewType } from "../api/types";
import { useT } from "../i18n";
import { PRIORITY_COLORS } from "../lib/colors";
import { addDays, dueLocal, monthGrid, monthName, weekdayName, weekStart } from "../lib/dates";
import { stripMarkdown } from "../lib/markdown";
import { agendaSort } from "../lib/tasks";
import { useApp } from "../store";
import type { QuickDefaults } from "../sync/quickSubmit";
import { TaskEditor } from "../tasks/TaskEditor";
import { openTask } from "../tasks/TaskRow";
import { IconChevronLeft, IconChevronRight, IconPlus } from "../ui/icons";
import { usePrefs } from "../ui/prefs";
import { Modal } from "../ui/primitives";
import { setOption } from "./viewOptions";

/** Basic week / month calendar: tasks placed on their due day. */
export function CalendarView({
  tasks,
  layout,
  viewType,
  objectId,
  addDefaults,
}: {
  tasks: Item[];
  layout: "WEEK" | "MONTH";
  viewType: ViewType;
  objectId: string | null;
  addDefaults: QuickDefaults;
}) {
  const prefs = usePrefs();
  const t = useT();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(prefs.today);
  const [adding, setAdding] = useState<string | null>(null);
  const days =
    layout === "WEEK"
      ? Array.from({ length: 7 }, (_, i) => addDays(weekStart(anchor, prefs.startDay), i))
      : monthGrid(Number(anchor.slice(0, 4)), Number(anchor.slice(5, 7)), prefs.startDay);
  const byDay = new Map<string, Item[]>();
  for (const it of tasks) {
    if (!it.due) continue;
    const d = dueLocal(it.due, prefs.tz).day;
    const list = byDay.get(d) ?? [];
    list.push(it);
    byDay.set(d, list);
  }
  const shift = (dir: number) => {
    if (layout === "WEEK") setAnchor(addDays(anchor, 7 * dir));
    else {
      const y = Number(anchor.slice(0, 4));
      const m = Number(anchor.slice(5, 7)) - 1 + dir;
      const yy = y + Math.floor(m / 12);
      const mm = ((m % 12) + 12) % 12;
      setAnchor(`${yy}-${String(mm + 1).padStart(2, "0")}-01`);
    }
  };
  const y = Number(anchor.slice(0, 4));
  const m = Number(anchor.slice(5, 7));
  return (
    <div data-testid="calendar" data-layout={layout}>
      <div className="cal-head">
        <strong>{prefs.lang === "zh" ? `${y}年${m}月` : `${monthName(m, "en", true)} ${y}`}</strong>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <button
            type="button"
            className={`btn btn-chip${layout === "WEEK" ? " active" : ""}`}
            onClick={() => setOption(viewType, objectId, { calendar_settings: { layout: "WEEK" } })}
          >
            {t("week")}
          </button>
          <button
            type="button"
            className={`btn btn-chip${layout === "MONTH" ? " active" : ""}`}
            onClick={() => setOption(viewType, objectId, { calendar_settings: { layout: "MONTH" } })}
          >
            {t("month")}
          </button>
          <button type="button" className="icon-btn" onClick={() => shift(-1)} aria-label={t("previous")}>
            <IconChevronLeft size={18} />
          </button>
          <button type="button" className="btn btn-chip" onClick={() => setAnchor(prefs.today)}>
            {t("goToday")}
          </button>
          <button type="button" className="icon-btn" onClick={() => shift(1)} aria-label={t("next")}>
            <IconChevronRight size={18} />
          </button>
        </div>
      </div>
      <div className={`cal-grid ${layout === "WEEK" ? "week" : "month"}`}>
        {days.slice(0, 7).map((d) => (
          <div key={`h${d}`} className="cal-weekday">
            {weekdayName(d, prefs.lang, true)}
          </div>
        ))}
        {days.map((d) => (
          <div
            key={d}
            className={`cal-cell${d === prefs.today ? " today" : ""}${layout === "MONTH" && d.slice(0, 7) !== anchor.slice(0, 7) ? " other" : ""}`}
            data-day={d}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="day-num">{layout === "WEEK" ? `${Number(d.slice(8))}` : Number(d.slice(8))}</span>
              <button
                type="button"
                className="icon-btn"
                style={{ width: 20, height: 20 }}
                onClick={() => setAdding(d)}
                aria-label={t("addTask")}
              >
                <IconPlus size={14} />
              </button>
            </div>
            {agendaSort(byDay.get(d) ?? [], prefs.tz).map((it) => {
              const time = it.due ? dueLocal(it.due, prefs.tz).time : null;
              return (
                <button
                  key={it.id}
                  type="button"
                  className="cal-task"
                  style={{ borderLeftColor: PRIORITY_COLORS[it.priority] }}
                  onClick={() => {
                    useApp.getState().set({ focusedTaskId: it.id });
                    openTask(navigate, it.id);
                  }}
                  title={stripMarkdown(it.content)}
                >
                  {time ? `${time} ` : ""}
                  {stripMarkdown(it.content)}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {adding && (
        <Modal onClose={() => setAdding(null)} className="quick-add-modal" label={t("addTask")}>
          <TaskEditor
            variant="modal"
            closeOnSubmit
            defaults={{ ...addDefaults, due: { date: adding } }}
            onClose={() => setAdding(null)}
          />
        </Modal>
      )}
    </div>
  );
}
